"use client";

import { useState, useEffect } from "react";
import { QuizContent, QuizState, QuizStats, DailyChallengeState } from "@/lib/daily/types";
import {
  loadQuizState,
  saveQuizState,
  loadQuizStats,
  saveQuizStats,
  loadDailyChallengeState,
  saveDailyChallengeState,
} from "@/lib/daily/storageManager";
import { updateQuizStats } from "@/lib/daily/scoreCalculator";
import { isValidAnswerIndex } from "@/lib/daily/validators/quizValidator";
import QuizQuestion from "./QuizQuestion";
import QuizResults from "./QuizResults";
import { ArrowLeft, ArrowRight, RefreshCw, CheckSquare, CheckCircle2, AlertCircle } from "lucide-react";
import Link from "next/link";
import YouMightAlsoLike from "@/components/blog/YouMightAlsoLike";
import { getDynamicYMALItems, type YMALPoolItem } from "@/lib/blog/ymal-mapper";

interface QuizOfDayProps {
  readonly content: QuizContent;
  readonly date: string;
  readonly mode?: "daily" | "practice";
  // Light blog pool computed server-side (see DailyHub.ymalPool)
  readonly ymalPool?: readonly YMALPoolItem[];
}

export default function QuizOfDay({ content, date, mode = "daily", ymalPool = [] }: QuizOfDayProps) {
  const [quizState, setQuizState] = useState<QuizState | null>(null);
  const [stats, setStats] = useState<QuizStats | null>(null);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [isQuestionSubmitted, setIsQuestionSubmitted] = useState(false);
  const [autoAdvanceTimer, setAutoAdvanceTimer] = useState<number>(5);
  const [isAutoAdvancing, setIsAutoAdvancing] = useState(false);

  // Load state on mount
  useEffect(() => {
    // Load stats
    const loadedStats = loadQuizStats();
    setStats(loadedStats);

    // Load quiz state
    const savedState = loadQuizState(date);
    if (savedState && savedState.date === date) {
      setQuizState(savedState);
      // Restore transient UI state for the current question index
      const activeQ = content.questions[savedState.currentQuestionIndex];
      const answer = savedState.answers[activeQ.id];
      if (answer !== undefined) {
        setSelectedOption(answer);
        setIsQuestionSubmitted(true);
      }
    } else {
      const newState: QuizState = {
        date,
        currentQuestionIndex: 0,
        answers: {},
        score: 0,
        status: "in_progress",
        startTime: Date.now(),
      };
      saveQuizState(newState);
      setQuizState(newState);
    }
  }, [date, content]);

  const handleSelectOption = (index: number) => {
    if (isValidAnswerIndex(index)) {
      setSelectedOption(index);
    }
  };

  const handleSubmitQuestion = () => {
    if (!quizState || !isValidAnswerIndex(selectedOption) || isQuestionSubmitted) return;

    const currentQuestions = content.questions;
    const activeQ = currentQuestions[quizState.currentQuestionIndex];
    const isCorrect = selectedOption === activeQ.correctIndex;

    const updatedAnswers = {
      ...quizState.answers,
      [activeQ.id]: selectedOption as number,
    };

    const newScore = quizState.score + (isCorrect ? 1 : 0);
    const updatedState: QuizState = {
      ...quizState,
      answers: updatedAnswers,
      score: newScore,
    };

    saveQuizState(updatedState);
    setQuizState(updatedState);
    setIsQuestionSubmitted(true);
    
    // Start 5-second auto-advance timer
    setAutoAdvanceTimer(5);
    setIsAutoAdvancing(true);
  };

  // Auto-advance timer effect
  useEffect(() => {
    if (!isAutoAdvancing || !isQuestionSubmitted) return;

    if (autoAdvanceTimer <= 0) {
      handleNextQuestion();
      setIsAutoAdvancing(false);
      return;
    }

    const timer = setTimeout(() => {
      setAutoAdvanceTimer((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [autoAdvanceTimer, isAutoAdvancing, isQuestionSubmitted]);

  const handleNextQuestion = () => {
    if (!quizState || !isQuestionSubmitted) return;

    // Stop auto-advance timer
    setIsAutoAdvancing(false);
    setAutoAdvanceTimer(5);

    const nextIndex = quizState.currentQuestionIndex + 1;
    const currentQuestions = content.questions;

    if (nextIndex < currentQuestions.length) {
      // Navigate to next question, resetting selection state
      const nextQ = currentQuestions[nextIndex];
      const savedAnswer = quizState.answers[nextQ.id];

      const updatedState: QuizState = {
        ...quizState,
        currentQuestionIndex: nextIndex,
      };

      saveQuizState(updatedState);
      setQuizState(updatedState);

      if (savedAnswer !== undefined) {
        setSelectedOption(savedAnswer);
        setIsQuestionSubmitted(true);
      } else {
        setSelectedOption(null);
        setIsQuestionSubmitted(false);
      }
    } else {
      // Completed all questions!
      const finalState: QuizState = {
        ...quizState,
        status: "completed",
        endTime: Date.now(),
      };

      saveQuizState(finalState);
      setQuizState(finalState);

      // Mark completed on the overall hub (only in daily mode)
      if (mode === "daily") {
        const hubState = loadDailyChallengeState();
        
        let streak = hubState.streak;
        const wasCompleted = hubState.quizCompleted;

        if (!wasCompleted) {
          // Update quiz stats
          if (stats) {
            const updatedStats = updateQuizStats(stats, currentQuestions, finalState.answers, date);
            saveQuizStats(updatedStats);
            setStats(updatedStats);
          }

          const isAllOtherDone = hubState.wordCompleted;
          let lastCompletedDate = hubState.lastCompletedDate;
          if (isAllOtherDone) {
            streak += 1;
            lastCompletedDate = date;
          }

          const updatedHub: DailyChallengeState = {
            ...hubState,
            quizCompleted: true,
            streak,
            lastCompletedDate,
          };
          saveDailyChallengeState(updatedHub);

          // Sync tabs
          if (typeof window !== "undefined") {
            window.dispatchEvent(new Event("storage"));
          }
        }
      }
    }
  };

  const handlePreviousQuestion = () => {
    if (!quizState || quizState.currentQuestionIndex === 0) return;

    // Stop auto-advance timer
    setIsAutoAdvancing(false);
    setAutoAdvanceTimer(5);

    const prevIndex = quizState.currentQuestionIndex - 1;
    const prevQ = content.questions[prevIndex];
    const savedAnswer = quizState.answers[prevQ.id];

    const updatedState: QuizState = {
      ...quizState,
      currentQuestionIndex: prevIndex,
    };

    saveQuizState(updatedState);
    setQuizState(updatedState);

    if (savedAnswer !== undefined) {
      setSelectedOption(savedAnswer);
      setIsQuestionSubmitted(true);
    } else {
      setSelectedOption(null);
      setIsQuestionSubmitted(false);
    }
  };

  if (!quizState || !stats) {
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        <RefreshCw size={24} className="icon-spin" />
        <p>Loading quiz state...</p>
      </div>
    );
  }

  const isCompleted = quizState.status === "completed";
  const currentQuestions = content.questions;
  const activeQuestion = currentQuestions[quizState.currentQuestionIndex];
  const progressPercent = ((quizState.currentQuestionIndex + (isQuestionSubmitted ? 1 : 0)) / currentQuestions.length) * 100;

  return (
    <>
      <div className="back-link-wrapper" style={{ marginBottom: "0.5rem" }}>
        <Link href="/daily" className="back-link">
          <ArrowLeft size={16} /> Back to Daily Challenge Hub
        </Link>
      </div>

      <div className="quiz-wrapper">

      {/* Practice Mode Badge */}
      {mode === "practice" && (
        <div
          style={{
            backgroundColor: "var(--secondary-light)",
            border: "1px solid var(--secondary-a30)",
            borderRadius: "var(--radius-md)",
            padding: "8px 16px",
            marginBottom: "1rem",
            textAlign: "center",
            color: "var(--info)",
            fontWeight: "var(--fw-bold)",
            fontSize: "var(--text-sm)",
          }}
        >
          🎮 Practice Mode — Unlimited
        </div>
      )}

      {isCompleted ? (
        <QuizResults
          questions={currentQuestions}
          answers={quizState.answers}
          stats={stats}
          mode={mode}
        />
      ) : (
        <div className="quiz-layout">
          <div className="quiz-main-column">
            <div className="daily-content-card">
              {/* Progress Bar */}
              <div className="quiz-progress-bar-container">
                <div
                  className="quiz-progress-bar-fill"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>

              <div className="quiz-question-info">
                Question {quizState.currentQuestionIndex + 1} of {currentQuestions.length}
              </div>

              <QuizQuestion
                question={activeQuestion}
                selectedOptionIndex={selectedOption}
                isSubmitted={isQuestionSubmitted}
                onSelectOption={handleSelectOption}
              />

              {/* Nav Controls */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "1.25rem",
                  paddingTop: "1rem",
                  borderTop: "1px solid var(--border-color)",
                }}
              >
                <button
                  onClick={handlePreviousQuestion}
                  disabled={quizState.currentQuestionIndex === 0}
                  className="btn btn-secondary"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    opacity: quizState.currentQuestionIndex === 0 ? 0.4 : 1,
                    cursor: quizState.currentQuestionIndex === 0 ? "not-allowed" : "pointer",
                    background: "transparent",
                    border: "1px solid var(--border-color)",
                    padding: "8px 16px",
                    borderRadius: "var(--radius-full)",
                    color: "var(--text-primary)",
                    fontWeight: "var(--fw-bold)"
                  }}
                >
                  <ArrowLeft size={16} />
                  <span>Back</span>
                </button>

                {!isQuestionSubmitted ? (
                  <button
                    onClick={handleSubmitQuestion}
                    disabled={selectedOption === null}
                    className="btn btn-primary"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      opacity: selectedOption === null ? 0.5 : 1,
                      cursor: selectedOption === null ? "not-allowed" : "pointer",
                      backgroundColor: "var(--primary)",
                      border: "none",
                      padding: "8px 20px",
                      borderRadius: "var(--radius-full)",
                      color: "var(--text-on-accent)",
                      fontWeight: "var(--fw-extrabold)"
                    }}
                  >
                    <CheckSquare size={16} />
                    <span>Submit Answer</span>
                  </button>
                ) : (
                  <button
                    onClick={handleNextQuestion}
                    className="btn btn-primary"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      cursor: "pointer",
                      backgroundColor: "var(--primary)",
                      border: "none",
                      padding: "8px 20px",
                      borderRadius: "var(--radius-full)",
                      color: "var(--text-on-accent)",
                      fontWeight: "var(--fw-extrabold)",
                      position: "relative"
                    }}
                  >
                    <span>
                      {quizState.currentQuestionIndex === currentQuestions.length - 1
                        ? "Finish Quiz"
                        : `Next Question${isAutoAdvancing ? ` (${autoAdvanceTimer}s)` : ""}`}
                    </span>
                    <ArrowRight size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Explanation Side Card */}
          {isQuestionSubmitted && (
            <div className="quiz-explanation-column anim-slide-up">
              <div className="daily-content-card quiz-explanation-card">
                <div className="quiz-explanation-title" style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: "var(--fw-extrabold)", fontSize: "var(--text-base)", color: selectedOption === activeQuestion.correctIndex ? "var(--primary)" : "var(--danger)", marginBottom: "0.5rem" }}>
                  {selectedOption === activeQuestion.correctIndex ? (
                    <>
                      <CheckCircle2 size={20} />
                      <span>Correct Answer!</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle size={20} />
                      <span>Incorrect Answer</span>
                    </>
                  )}
                </div>
                <div className="quiz-explanation-text" style={{ fontSize: "var(--text-sm)", lineHeight: 1.5, color: "var(--text-secondary)" }}>
                  {activeQuestion.explanation}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="section-divider"></div>
      <YouMightAlsoLike noMargin={true} items={getDynamicYMALItems([...ymalPool], "quiz-" + date)} />
    </div>
    </>
  );
}
